import React, { useState } from 'react';
import { useAppSelector } from '../../hooks/useAppSelector';
import CookHome from '../../components/cook/CookHome';
import CookAttendance from '../../components/cook/CookAttendance';
import CookLiveOrders from '../../components/cook/CookLiveOrders';
import CookPerformance from '../../components/cook/CookPerformance';
import Layout from '../../components/Layout';
import Toast from '../../components/Toast';
import { normalizeRestaurantId } from '../../utils/restaurantUtils';

const CookDashboard = () => {
    const { user, restaurant } = useAppSelector(state => state.auth);
    const cookId = user?.staffId || user?.id;
    // Normalize restaurantId to ensure it's always a string
    const restaurantId = normalizeRestaurantId(user?.restaurantId || restaurant?._id || restaurant);
    
    const [activeTab, setActiveTab] = useState('live'); // Default to live orders for kitchen
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [homeRefreshKey, setHomeRefreshKey] = useState(0);

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    };

    const tabs = [
        { id: 'live', label: 'Kitchen Display', icon: '🍳', shortLabel: 'KDS' },
        { id: 'home', label: 'Dashboard', icon: '🏠', shortLabel: 'Home' },
        { id: 'attendance', label: 'Attendance', icon: '📅', shortLabel: 'Check-in' },
        { id: 'performance', label: 'Performance', icon: '📊', shortLabel: 'Stats' }
    ];

    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        // Refresh home stats when navigating back to home tab
        if (tabId === 'home') {
            setHomeRefreshKey(prev => prev + 1);
        }
    };

    const renderContent = () => {
        switch (activeTab) {
            case 'home':
                return <CookHome key={homeRefreshKey} cookId={cookId} restaurantId={restaurantId} onNavigate={handleTabChange} />;
            case 'attendance':
                return <CookAttendance cookId={cookId} restaurantId={restaurantId} />;
            case 'live':
                return <CookLiveOrders cookId={cookId} restaurantId={restaurantId} />;
            case 'performance':
                return <CookPerformance cookId={cookId} restaurantId={restaurantId} />;
            default:
                return <CookLiveOrders cookId={cookId} restaurantId={restaurantId} />;
        }
    };

    return (
        <Layout>
            <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50">
                {/* Header */}
                <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-lg sticky top-0 z-50">
                    <div className="container mx-auto px-3 sm:px-4 lg:px-6 py-3 sm:py-4">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
                            <div className="flex items-center gap-3">
                                <div className="bg-white/20 rounded-lg p-2 sm:p-3">
                                    <span className="text-2xl sm:text-3xl">👨‍🍳</span>
                                </div>
                                <div>
                                    <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold">Kitchen Display System</h1>
                                    <p className="text-xs sm:text-sm text-orange-100">Welcome, {user?.name || 'Chef'}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                                <span className="hidden sm:inline">Live</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tab Navigation - Mobile Optimized */}
                <div className="bg-white shadow-md sticky top-[73px] sm:top-[81px] z-40 border-b">
                    <div className="container mx-auto px-2 sm:px-4">
                        <div className="flex overflow-x-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => handleTabChange(tab.id)}
                                    className={`flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-3 sm:px-6 py-3 sm:py-4 font-semibold transition-all duration-300 whitespace-nowrap min-w-[80px] sm:min-w-0 flex-shrink-0 border-b-3 ${
                                        activeTab === tab.id
                                            ? 'text-orange-600 border-orange-600 bg-orange-50'
                                            : 'text-gray-600 border-transparent hover:text-orange-600 hover:bg-orange-50'
                                    }`}
                                >
                                    <span className="text-xl sm:text-lg">{tab.icon}</span>
                                    <span className="text-xs sm:text-sm font-medium">{tab.shortLabel || tab.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                
                {/* Content */}
                <div className="container mx-auto px-2 sm:px-4 lg:px-6 py-4 sm:py-6">
                    {renderContent()}
                </div>
            </div>

            {toast.show && <Toast message={toast.message} type={toast.type} />}
        </Layout>
    );
};

export default CookDashboard;
